/**
 * Raseborg Menu System
 * Implements a hierarchical, animated mobile-style menu with two layers for smooth transitions.
 */

enum Action {
  Open = 'open',
  Close = 'close',
}

enum Layer {
  Base = 'base-menu',
  Slide = 'slide-menu',
}

class MenuSystem {
  private container: HTMLElement | null = null;
  private baseMenu: HTMLElement | null = null;
  private slideMenu: HTMLElement | null = null;
  private templates: HTMLElement | null = null;
  private body: HTMLElement = document.body;
  private isAnimating: boolean = false;

  constructor() {
    this.init();
  }

  private init() {
    console.log('MenuSystem: Initializing...');
    this.container = document.getElementById('menu-container');
    this.baseMenu = document.getElementById('base-menu');
    this.slideMenu = document.getElementById('slide-menu');
    this.templates = document.getElementById('templates');

    if (!this.container || !this.baseMenu || !this.slideMenu || !this.templates) {
      console.error('MenuSystem: Missing required elements', {
        container: !!this.container,
        baseMenu: !!this.baseMenu,
        slideMenu: !!this.slideMenu,
        templates: !!this.templates
      });
      return;
    }

    this.bindEvents();
    this.setupInitialState();
  }

  private setupInitialState() {
    // Load root template into base menu initially
    const rootTemplate = document.getElementById('template-root');
    console.log('MenuSystem: Setting up initial state...', { rootTemplate: !!rootTemplate });
    if (rootTemplate && this.baseMenu) {
      this.copyMenu(rootTemplate, this.baseMenu);
      this.bindMenuButtons(this.baseMenu);
      this.baseMenu.classList.add('show');
    } else {
      console.warn('MenuSystem: Root template or base menu not found');
    }
  }

  private bindEvents() {
    // Global triggers (e.g., hamburger button)
    const triggers = document.querySelectorAll('[data-menu-toggle]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMenu();
      });
    });

    // Close on backdrop click if needed
    this.container?.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.toggleMenu(false);
      }
    });
  }

  private bindMenuButtons(container: HTMLElement) {
    // Forward buttons
    const openButtons = container.querySelectorAll('[data-menu-open]');
    openButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = (btn as HTMLElement).dataset.menuOpen;
        if (targetId) {
          this.transition(Action.Open, targetId);
        }
      });
    });

    // Backward buttons
    const closeButtons = container.querySelectorAll('[data-menu-back]');
    closeButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = (btn as HTMLElement).dataset.menuBack;
        if (targetId) {
          this.transition(Action.Close, targetId);
        }
      });
    });
  }

  private toggleMenu(force?: boolean) {
    if (!this.container) return;
    const willOpen = force !== undefined ? force : !this.container.classList.contains('open');
    const triggers = document.querySelectorAll('[data-menu-toggle]');

    if (willOpen) {
      this.container.classList.add('open');
      this.body.classList.add('noscroll');
      triggers.forEach((trigger) => trigger.classList.add('open'));
    } else {
      this.container.classList.remove('open');
      this.body.classList.remove('noscroll');
      triggers.forEach((trigger) => trigger.classList.remove('open'));
    }
  }

  private async transition(action: Action, templateId: string) {
    if (this.isAnimating || !this.baseMenu || !this.slideMenu) return;

    const fullId = templateId === 'root' ? 'template-root' : `template-${templateId}`;
    console.log(`MenuSystem: Transitioning ${action} to ${fullId}`);
    const targetTemplate = document.getElementById(fullId);
    if (!targetTemplate) {
      console.error(`MenuSystem: Template not found: template-${templateId}`);
      return;
    }

    this.isAnimating = true;

    if (action === Action.Open) {
      await this.transitionForward(targetTemplate);
    } else {
      await this.transitionBackward(targetTemplate);
    }

    this.isAnimating = false;
  }

  private transitionForward(template: HTMLElement) {
    return new Promise<void>((resolve) => {
      if (!this.slideMenu || !this.baseMenu) return resolve();

      // 1. Prepare slide menu at the right
      this.slideMenu.classList.remove('animate');
      this.slideMenu.classList.add('right');
      this.copyMenu(template, this.slideMenu);
      this.slideMenu.classList.add('show');

      // 2. Trigger animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.slideMenu?.classList.add('animate');
          this.slideMenu?.classList.remove('right');
        });
      });

      // 3. Cleanup after transition
      setTimeout(() => {
        if (this.slideMenu && this.baseMenu) {
          this.copyMenu(this.slideMenu, this.baseMenu);
          this.bindMenuButtons(this.baseMenu);
          this.slideMenu.classList.remove('show', 'animate');
        }
        resolve();
      }, 300); // Matches CSS transition duration
    });
  }

  private transitionBackward(template: HTMLElement) {
    return new Promise<void>((resolve) => {
      if (!this.slideMenu || !this.baseMenu) return resolve();

      // 1. Move current base content to slide menu (the one that will slide out)
      this.slideMenu.classList.remove('animate', 'right');
      this.copyMenu(this.baseMenu, this.slideMenu);
      this.slideMenu.classList.add('show');

      // 2. Update base menu with parent content (hidden behind slide menu)
      this.copyMenu(template, this.baseMenu);
      this.bindMenuButtons(this.baseMenu);

      // 3. Trigger slide out animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.slideMenu?.classList.add('animate');
          this.slideMenu?.classList.add('right');
        });
      });

      // 4. Cleanup
      setTimeout(() => {
        this.slideMenu?.classList.remove('show', 'animate', 'right');
        resolve();
      }, 300);
    });
  }

  private copyMenu(from: HTMLElement, to: HTMLElement) {
    this.emptyMenu(to);
    const clone = from.cloneNode(true) as HTMLElement;
    while (clone.firstChild) {
      to.appendChild(clone.firstChild);
    }
  }

  private emptyMenu(container: HTMLElement) {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  new MenuSystem();
});
